import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../services/firebase/config';
import { getCurrentUserId, fixAzureUserId } from './authUtils';

// Vérifier si nous sommes en environnement de développement 
const isDevEnv = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// Option pour forcer l'utilisation de Firebase réel même en développement
// Mettez cette variable à true pour utiliser le vrai Firebase Storage
const USE_REAL_FIREBASE = true;

/**
 * Upload un fichier vers Firebase Storage et retourne l'URL de téléchargement
 * @param {File} file - Le fichier à uploader
 * @param {string} path - Le chemin dans Storage (sans le préfixe utilisateur)
 * @param {Function} progressCallback - Callback pour suivre la progression (optionnel)
 * @returns {Promise<string>} - L'URL de téléchargement du fichier
 */
export const uploadFile = async (file, path, progressCallback = null) => {
  try {
    // Obtenir l'ID utilisateur avec notre utilitaire
    const userId = getCurrentUserId();
    
    // Si nous n'avons pas d'ID utilisateur, lancer une erreur
    if (!userId) {

      throw new Error('Utilisateur non authentifié');
    }
    
    // Ajouter le préfixe 'azure-' si nécessaire
    const normalizedUserId = fixAzureUserId(userId);

    // En mode de développement ou si les tests montrent des problèmes d'autorisation,
    // nous pouvons simuler l'upload et retourner une URL factice
    if (isDevEnv && !USE_REAL_FIREBASE) {
      
      // Simuler un délai et une progression
      return new Promise((resolve) => {
        let progress = 0;
        const interval = setInterval(() => {
          progress += 10;
          
          if (progressCallback) {
            progressCallback(progress);
          }

          if (progress >= 100) {
            clearInterval(interval);
            
            // URL simulée
            const fakeUrl = `https://storage.googleapis.com/mock-diotranscript/${normalizedUserId}/${path}/${file.name}`;
            
            resolve(fakeUrl);
          }
        }, 300);
      });
    }
    
    // Créer un chemin unique pour le fichier
    const timestamp = new Date().getTime();
    const fileExtension = file.name.split('.').pop();
    const fileName = `${timestamp}.${fileExtension}`;
    
    // Le chemin complet dans Firebase Storage 
    const fullPath = `users/${normalizedUserId}/${path}/${fileName}`;

    // Créer une référence au fichier dans Storage
    const storageRef = ref(storage, fullPath);
    
    // Démarrer l'upload
    const uploadTask = uploadBytesResumable(storageRef, file);
    
    // Retourner une promesse qui se résout quand l'upload est terminé
    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Calculer la progression
          const progress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );

          // Appeler le callback de progression si fourni
          if (progressCallback) {
            progressCallback(progress);
          }
        },
        (error) => {
          // Gérer les erreurs

          // Si nous avons une erreur d'autorisation et que nous sommes en développement,
          // nous pouvons retourner une URL fictive au lieu d'échouer
          if (error.code === 'storage/unauthorized' && isDevEnv && !USE_REAL_FIREBASE) {

            const fakeUrl = `https://storage.googleapis.com/mock-diotranscript/${normalizedUserId}/${path}/${fileName}`;
            resolve(fakeUrl);
          } else {
            reject(error);
          }
        },
        async () => {
          // Upload terminé avec succès
          try {
            // Obtenir l'URL de téléchargement
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          } catch (error) {

            // Si nous avons une erreur d'autorisation lors de la récupération de l'URL
            if (error.code === 'storage/unauthorized' && isDevEnv && !USE_REAL_FIREBASE) {

              const fakeUrl = `https://storage.googleapis.com/mock-diotranscript/${normalizedUserId}/${path}/${fileName}`;
              resolve(fakeUrl);
            } else {
              reject(error);
            }
          }
        }
      );
    });
  } catch (error) {

    // Si nous sommes en développement, générer une URL fictive plutôt que d'échouer
    if (isDevEnv && !USE_REAL_FIREBASE) {

      return `https://storage.googleapis.com/mock-diotranscript/error-fallback/${Date.now()}.${file.name.split('.').pop()}`;
    }
    
    throw error;
  }
}; 